import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LaborRatesService } from '../services/laborRates.service';
import type { LaborRate, LaborRateFilters } from '../types/laborRates.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useLaborRates(companyId: string, initialFilters: LaborRateFilters = {}) {
  const [filters, setFilters] = useState<LaborRateFilters>(initialFilters);

  const query = useQuery({
    queryKey: ['labor-rates', companyId, filters],
    queryFn: () => LaborRatesService.getLaborRates(companyId, filters),
    enabled: !!companyId,
  });

  return {
    ...query,
    filters,
    setFilters
  };
}

export function useUserLaborRates(userId?: string) {
  return useQuery({
    queryKey: ['user-labor-rates', userId],
    queryFn: () => LaborRatesService.getUserLaborRates(userId!),
    enabled: !!userId,
  });
}

export function useMutateLaborRate() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (rate: Partial<LaborRate>) => LaborRatesService.createLaborRate(rate),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['labor-rates'] });
      if (variables.user_id) {
        queryClient.invalidateQueries({ queryKey: ['user-labor-rates', variables.user_id] });
      }
      toast.success('Đã lưu đơn giá thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi lưu đơn giá: ' + error.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<LaborRate> }) => 
      LaborRatesService.updateLaborRate(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['labor-rates'] });
      // We might need to invalidate user-labor-rates if we knew the user_id, but clearing all is safer
      queryClient.invalidateQueries({ queryKey: ['user-labor-rates'] });
      toast.success('Đã cập nhật đơn giá thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi cập nhật đơn giá: ' + error.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => LaborRatesService.deleteLaborRate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['labor-rates'] });
      queryClient.invalidateQueries({ queryKey: ['user-labor-rates'] });
      toast.success('Đã xóa đơn giá thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi xóa đơn giá: ' + error.message);
    }
  });

  return {
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
    isPending: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending
  };
}
